import clsx from 'clsx'
import { useState } from 'react'
import { useRouter } from 'next/router'
import { useInfiniteQuery } from '@tanstack/react-query'

import Search from '@/templates/Search/Search'
import { themeList } from '@/cmp/templates/Leaders/utils'
import useWindowSize from '@/hooks/useWindowSize'
import { getStrapiGraphqlData } from '@/utils/api/wrapper'
import { filteredLeadersPageQuery } from '@/utils/queries/influencer'
import InfluencerCard from '../InfluencerCard'
import FilterDropdown from './filterDropdown/FilterDropdown'

/* assets */
import { ReactComponent as TextDecoration } from 'public/assets/influencer/icons/text-decoration.svg'
import { ReactComponent as ArrowLeft } from 'public/assets/icons/chevron-right.svg'

interface Props {
  title?: string
  showFilters?: boolean
  showThemeFilter?: boolean
}

const initialFilter = {
  theme: undefined
}

function CMLList({
  title,
  showFilters = true,
  showThemeFilter = true
}: Props): JSX.Element {
  const router = useRouter()
  const { isMobile } = useWindowSize()
  const [filters, setFilters] = useState({ ...initialFilter })

  const leaderType = router.query.type === 'indian' ? 'Indian' : 'Global'
  const updatedTheme = leaderType === 'Global' ? undefined : filters.theme

  const influencerList = useInfiniteQuery(
    ['influencers', leaderType, { ...filters, theme: updatedTheme }],
    async ({ pageParam }) =>
      await getStrapiGraphqlData<IndividualInfluencer>(
        filteredLeadersPageQuery.loc?.source.body,
        { page: pageParam, speakerType: leaderType, theme: updatedTheme }
      ),
    {
      enabled: true,
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
      getNextPageParam: lastPage => {
        const pagination = lastPage.influencers.meta.pagination
        return pagination.pageCount > pagination.page
          ? pagination.page + 1
          : false
      }
    }
  )

  const handleScroll = async (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget
    if (scrollHeight - scrollTop - clientHeight < 1) {
      if (influencerList.hasNextPage) await influencerList.fetchNextPage()
    }
  }

  const influencerGroup =
    influencerList.data?.pages.map(data =>
      data.influencers.data.map(i => i.attributes)
    ) ?? []
  const influencers = ([] as IndividualInfluencerAttr[]).concat(
    ...influencerGroup
  )

  return (
    <section className="py-12">
      {title && (
        <div className="mb-3">
          <h2 className="text-2xl md:text-4xl font-medium text-center | mb-2 lg:mb-3">
            Get started with expert-backed insights
          </h2>
          <TextDecoration
            className="text-yellow-50 | mx-auto mb-10 lg:mb-12"
            aria-hidden
          />
        </div>
      )}

      {showFilters && (
        <div
          className={clsx(
            'flex flex-col md:flex-row gap-y-6 mb-16',
            showThemeFilter ? 'justify-between' : 'justify-center'
          )}
        >
          {showThemeFilter ? (
            <div className="flex gap-2 | w-full md:w-2/5">
              <FilterDropdown
                className="text-sm md:text-lg | flex flex-row flex-grow gap-4 lg:gap-6 | w-3/5 md:w-3/4"
                values={filters}
                themes={themeList}
                handleFilterChange={(key, value) =>
                  setFilters({ ...filters, [key]: value })
                }
              />
              {filters.theme ? (
                <button
                  className="text-sm underline underline-offset-4 | pl-4"
                  onClick={() => setFilters(initialFilter)}
                >
                  Clear Filter x
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="w-full md:w-2/5">
            <Search />
          </div>
        </div>
      )}

      <div
        className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8 | mb-20 | max-h-[580px] md:max-h-full | overflow-y-scroll md:overscroll-none"
        onScroll={e => (influencers.length < 32 ? handleScroll(e) : null)}
      >
        {influencers.length === 0 && (
          <div className="col-span-full flex items-center justify-center flex-col">
            <h3 className="text-2xl mb-4">No leaders found</h3>
            <button
              className="btn-secondary btn-size-default"
              onClick={() => setFilters(initialFilter)}
            >
              Clear Filters?
            </button>
          </div>
        )}
        {influencers.map(influencer => {
          const image =
            influencer.profileImage.data?.attributes ??
            influencer.image.data?.attributes

          if (!image) return

          return (
            <InfluencerCard
              key={influencer.slug}
              type={isMobile ? 'extendedSmall' : 'default'}
              image={image}
              name={influencer.name}
              designation={influencer.designation}
              description={influencer.description}
              slug={`/global-marketing-leaders/${influencer.slug}/`}
              themeColor={influencer.themeColor}
              links={{
                instagram: influencer.instagramLink,
                twitter: influencer.twitterLink,
                linkedin: influencer.linkedinLink
              }}
            />
          )
        })}
      </div>
      {isMobile ? null : (
        <div className="flex items-center justify-center">
          {influencerList.hasNextPage && (
            <button
              className="font-medium text-lg text-blue-web-50 bg-white | border border-web-blue-50 rounded-lg | flex items-center | px-6 py-4"
              onClick={() => influencerList.fetchNextPage()}
            >
              View More
              <ArrowLeft className="ml-3" />
            </button>
          )}
        </div>
      )}
    </section>
  )
}

export default CMLList
